import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Bookmark, BookmarkPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";

const FILTER_KEYS = ["q", "district", "concelho", "category", "regime", "work_mode", "days",
  "near_district", "near_concelho", "radius_km", "sort"];

function humanFilters(params, extra = null) {
  const parts = [];
  Object.values(extra || {}).forEach((v) => { if (v) parts.push(v); });
  const q = params.get("q"); if (q) parts.push(`"${q}"`);
  const d = params.get("district"); if (d) parts.push(d);
  const c = params.get("concelho"); if (c) parts.push(c);
  const cat = params.get("category"); if (cat) parts.push(cat);
  const reg = params.get("regime"); if (reg) parts.push(reg);
  const wm = params.get("work_mode"); if (wm) parts.push(wm);
  const nc = params.get("near_concelho"); if (nc) parts.push(`até ${params.get("radius_km") || 25} km de ${nc}`);
  return parts.length ? parts.join(" · ") : "Todas as vagas";
}

function suggestName(params, extra = null) {
  const x = extra || {};
  const q = params.get("q");
  const cat = params.get("category") || x.category;
  const nc = params.get("near_concelho");
  const loc = nc ? `${nc} (${params.get("radius_km") || 25} km)`
    : params.get("concelho") || params.get("district") || x.concelho || x.district;
  if (q && loc) return `${q} em ${loc}`;
  if (q) return q;
  if (cat && loc) return `${cat} em ${loc}`;
  if (cat) return cat;
  if (loc) return `Vagas em ${loc}`;
  return "A minha pesquisa";
}

export function SaveSearchButton({ extraFilters = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [alert, setAlert] = useState(true);
  const [saving, setSaving] = useState(false);

  const openModal = () => {
    if (!user) {
      navigate(`/auth?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (user.role !== "candidate") {
      toast.info("Só candidatos podem guardar pesquisas.");
      return;
    }
    setName(suggestName(params, extraFilters));
    setAlert(true);
    setOpen(true);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Dá um nome à pesquisa."); return; }
    const filters = {};
    // Filtros "fixos" da página (ex.: distrito em /empregos-em/braga) não estão no URL
    Object.entries(extraFilters || {}).forEach(([k, v]) => { if (v) filters[k] = v; });
    FILTER_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) filters[k] = ["days", "radius_km"].includes(k) ? Number(v) : v;
    });
    if (filters.near_concelho && !filters.radius_km) filters.radius_km = 25;
    setSaving(true);
    try {
      await api.post("/candidate/saved-searches", { name: name.trim(), filters, alert });
      toast.success(alert ? "Pesquisa guardada. Vais receber alertas!" : "Pesquisa guardada.");
      setOpen(false);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="save-search-btn"
        onClick={openModal}
        className="inline-flex items-center gap-1.5 rounded-full h-9 px-3 border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 shadow-sm transition"
      >
        <BookmarkPlus className="w-3.5 h-3.5" /> Guardar pesquisa
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading font-black tracking-tighter">
              <Bookmark className="w-4 h-4 text-amber-500" /> Guardar pesquisa
            </DialogTitle>
            <DialogDescription>
              Guarda esta combinação de filtros e sê avisado das vagas novas no sino de notificações e por email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Nome</Label>
              <Input
                data-testid="save-search-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
                placeholder="Ex.: Restauração em Lisboa"
              />
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Filtros aplicados</p>
              <p data-testid="save-search-summary" className="text-slate-700 font-medium">{humanFilters(params, extraFilters)}</p>
            </div>
            <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
              <Checkbox
                data-testid="save-search-alert-checkbox"
                checked={alert}
                onCheckedChange={setAlert}
                className="mt-0.5"
              />
              <span>
                <span className="font-semibold">Receber alertas</span> (notificação na plataforma + email) quando entrarem novas vagas que correspondam{params.get("near_concelho") ? ` — dentro de ${params.get("radius_km") || 25} km de ${params.get("near_concelho")}` : ""}.
              </span>
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
            <Button data-testid="save-search-submit" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
